import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Analytics } from "@vercel/analytics/react";
// import { env } from "node:process";


// --- Config ---
const API_BASE = process.env.REACT_APP_API_URL //|| "http://localhost:8080/api/v1";

// --- WebGL Shader Background Component ---
const ShaderBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas.getContext('webgl');
    if (!gl) return;

    const vertexShaderSource = `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragmentShaderSource = `
      precision highp float;
      uniform vec2 u_resolution;
      uniform float u_time;

      void main() {
        vec2 st = gl_FragCoord.xy / u_resolution.xy;
        st.x *= u_resolution.x / u_resolution.y;
        
        vec3 col1 = vec3(0.02, 0.02, 0.04);
        vec3 col2 = vec3(0.08, 0.05, 0.15);
        vec3 col3 = vec3(0.02, 0.08, 0.10);
        
        float q = sin(st.x * 1.5 + u_time * 0.1) * 0.5 + 0.5;
        float w = sin(st.y * 2.0 + u_time * 0.08 + q) * 0.5 + 0.5;
        float noise = sin(st.x * 4.0 + u_time * 0.05) * cos(st.y * 3.0 - u_time * 0.1);
        
        vec3 color = mix(col1, col2, w + noise * 0.3);
        color = mix(color, col3, q * noise);
        
        float grain = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898,78.233))) * 43758.5453123);
        color += grain * 0.012;
        
        gl_FragColor = vec4(color, 1.0);
      }
    `;

    const createShader = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      return shader;
    };

    const program = gl.createProgram();
    gl.attachShader(program, createShader(gl.VERTEX_SHADER, vertexShaderSource));
    gl.attachShader(program, createShader(gl.FRAGMENT_SHADER, fragmentShaderSource));
    gl.linkProgram(program);
    gl.useProgram(program);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    const resolutionLocation = gl.getUniformLocation(program, "u_resolution");
    const timeLocation = gl.getUniformLocation(program, "u_time");

    let animationFrameId;
    let startTime = Date.now();

    const render = () => {
      const displayWidth = canvas.clientWidth;
      const displayHeight = canvas.clientHeight;
      if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
        canvas.width = displayWidth;
        canvas.height = displayHeight;
        gl.viewport(0, 0, canvas.width, canvas.height);
      }
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform1f(timeLocation, (Date.now() - startTime) / 1000);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      animationFrameId = requestAnimationFrame(render);
    };

    render();
    return () => {
      cancelAnimationFrame(animationFrameId);
      gl.deleteProgram(program);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full -z-20 pointer-events-none"
    />
  );
};

// --- Utilities ---
const formatINR = (value, compact = false) => {
  if (value == null || isNaN(value)) return '—';
  if (compact) {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
};

// Stable color palette assigned to asset classes by index (backend has no colors)
const ASSET_COLORS = [
  "#60A5FA", "#818CF8", "#C084FC", "#F472B6",
  "#FBBF24", "#34D399", "#10B981", "#F87171",
  "#A78BFA", "#38BDF8", "#4ADE80", "#FB923C",
];

const generateSmoothPath = (data, width, height) => {
  if (!data || data.length < 2) return "";
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * height;
    return [x, y];
  });

  let path = `M ${points[0][0]},${points[0][1]} `;
  for (let i = 0; i < points.length - 1; i++) {
    const curr = points[i];
    const next = points[i + 1];
    const cp1x = curr[0] + (next[0] - curr[0]) / 2;
    const cp2x = curr[0] + (next[0] - curr[0]) / 2;
    path += `C ${cp1x},${curr[1]} ${cp2x},${next[1]} ${next[0]},${next[1]} `;
  }
  return path;
};

// --- UI Components ---

const ElegantSlider = ({ label, value, min, max, step, onChange, formatVal }) => {
  const percentage = ((value - min) / (max - min)) * 100;
  return (
    <div className="flex flex-col gap-2 mb-8 group">
      <div className="flex justify-between items-end">
        <label className="text-sm font-medium text-white/60 tracking-wide uppercase font-inter transition-colors group-hover:text-white/80">
          {label}
        </label>
        <span className="text-xl font-outfit font-light text-white tracking-wider">
          {formatVal(value)}
        </span>
      </div>
      <div className="relative h-1 bg-white/10 rounded-full overflow-hidden">
        <div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-white/20 to-white/80 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${percentage}%` }}
        />
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>
    </div>
  );
};

const StatCard = ({ label, value, subValue, highlightColor }) => (
  <div className="relative overflow-hidden p-6 rounded-[2rem] rounded-tr-md bg-white/[0.03] backdrop-blur-xl border border-white/5 transition-all duration-500 hover:bg-white/[0.05] hover:border-white/10 group">
    <div
      className="absolute -inset-[100%] opacity-0 group-hover:opacity-20 transition-opacity duration-700 blur-3xl rounded-full pointer-events-none"
      style={{ background: `radial-gradient(circle at center, ${highlightColor}, transparent 50%)` }}
    />
    <div className="relative z-10 flex flex-col gap-1">
      <span className="text-xs uppercase tracking-[0.2em] text-white/50 font-inter">{label}</span>
      <span className="text-3xl font-outfit font-light text-white mt-1">{value}</span>
      {subValue && (
        <span className="text-sm font-inter text-white/40 mt-1 flex items-center gap-2">
          <span className="w-1 h-1 rounded-full" style={{ backgroundColor: highlightColor }} />
          {subValue}
        </span>
      )}
    </div>
  </div>
);

const Sparkline = ({ data, color }) => {
  const width = 400;
  const height = 80;
  const pathData = useMemo(() => generateSmoothPath(data, width, height), [data]);

  return (
    <div className="relative w-full h-32 mt-6 flex items-end">
      <svg viewBox={`0 -10 ${width} ${height + 20}`} className="w-full h-full overflow-visible" preserveAspectRatio="none">
        <defs>
          <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.4" />
            <stop offset="100%" stopColor={color} stopOpacity="0.0" />
          </linearGradient>
          <filter id={`glow-${color}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>
        <path d={`${pathData} L ${width},${height} L 0,${height} Z`} fill={`url(#grad-${color})`} className="animate-fade-in" />
        <path d={pathData} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
          filter={`url(#glow-${color})`} className="draw-path" style={{ '--path-length': '1500px' }} />
      </svg>
    </div>
  );
};

const LoadingPulse = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-20 w-2/3 bg-white/5 rounded-2xl" />
    <div className="h-32 bg-white/5 rounded-2xl" />
    <div className="grid grid-cols-2 gap-4">
      {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-white/5 rounded-3xl" />)}
    </div>
  </div>
);

// --- API Hooks ---

function useAssetClasses() {
  const [assetClasses, setAssetClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/assets`)
      .then(r => {
        if (!r.ok) throw new Error(`Failed to load assets: ${r.status}`);
        return r.json();
      })
      .then(data => {
        // Attach stable colors by index since backend doesn't provide them
        const withColors = data.map((ac, i) => ({
          ...ac,
          color: ASSET_COLORS[i % ASSET_COLORS.length],
        }));
        setAssetClasses(withColors);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return { assetClasses, loading, error };
}

function usePortfolioProjection(assets, age, retireAge) {
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounce API calls — wait 600ms after last change
  useEffect(() => {
    const years = retireAge - age;
    if (years <= 0 || assets.length === 0) {
      setProjection(null);
      return;
    }

    setLoading(true);
    setError(null);

    const timer = setTimeout(() => {
      const body = {
        assets: assets.map(a => ({
          asset_id: a.typeId,
          current_value: a.lumpsum,
          monthly_sip: a.sip,
          time_horizon_years: years,
          step_up_rate: 0,           // expose as a slider later if needed
        })),
        run_monte_carlo: false,       // keep fast; toggle in settings later
      };

      // debug log
      console.log("Requesting projection with body:", JSON.stringify(body));

      fetch(`${API_BASE}/calculate/portfolio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
        .then(r => {
          if (!r.ok) return r.json().then(e => { throw new Error(e.detail || `API error ${r.status}`); });
          
          // debug log
          console.log("Received projection response:", r);
          
          return r.json();
        })
        .then(data => setProjection(data))
        .catch(e => {setError(e.message); console.error("Projection error:", e);})
        .finally(() => setLoading(false));
    }, 600);

    return () => clearTimeout(timer);
  }, [assets, age, retireAge]);

  return { projection, loading, error };
}

// --- Main Application ---

export default function App() {
  const [age, setAge] = useState(30);
  const [retireAge, setRetireAge] = useState(60);

  // Asset classes come from the backend
  const { assetClasses, loading: assetsLoading, error: assetsError } = useAssetClasses();

  const [assets, setAssets] = useState([
    { id: 1, name: 'Core Mutual Fund', sip: 25000, lumpsum: 100000, typeId: 'equity_largecap' }
  ]);

  // debug
  console.log("Backend api base URL:", API_BASE);

  // Sync default asset typeId once assetClasses arrive
  useEffect(() => {
    if (assetClasses.length > 0) {
      setAssets(prev => prev.map(a => ({
        ...a,
        typeId: assetClasses.find(ac => ac.id === a.typeId)
          ? a.typeId
          : assetClasses[0].id,
      })));
    }
  }, [assetClasses]);

  const addAsset = () => {
    const defaultType = assetClasses[0]?.id || 'equity_largecap';
    setAssets([...assets, { id: Date.now(), name: 'New Asset', sip: 5000, lumpsum: 0, typeId: defaultType }]);
  };

  const updateAsset = (id, field, value) =>
    setAssets(assets.map(a => a.id === id ? { ...a, [field]: value } : a));

  const removeAsset = (id) => {
    if (assets.length > 1) setAssets(assets.filter(a => a.id !== id));
  };

  // Projection from backend
  const { projection, loading: projLoading, error: projError } = usePortfolioProjection(assets, age, retireAge);

  // Build sparkline data from backend yearly_snapshots (sum net_corpus across all assets per year)
  const sparklineData = useMemo(() => {
    if (!projection?.assets?.length) return null;

    const yearCount = projection.assets[0].yearly_snapshots.length;
    const aggregated = new Array(yearCount).fill(0);

    projection.assets.forEach(asset => {
      asset.yearly_snapshots.forEach((snap, i) => {
        aggregated[i] += snap.corpus_value ?? snap.real_value ?? 0;
      });
    });

    return aggregated;
  }, [projection]);

  // Color based on first asset's type
  const accentColor = useMemo(() => {
    const match = assetClasses.find(ac => ac.id === assets[0]?.typeId);
    return match?.color || '#60A5FA';
  }, [assetClasses, assets]);

  // Inject fonts and keyframes
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500&family=Outfit:wght@200;300;400;600&display=swap');
      .font-inter { font-family: 'Inter', sans-serif; }
      .font-outfit { font-family: 'Outfit', sans-serif; }
      .bg-grid-pattern {
        background-image: radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px);
        background-size: 32px 32px;
      }
      @keyframes drawPath {
        from { stroke-dashoffset: var(--path-length); stroke-dasharray: var(--path-length); }
        to { stroke-dashoffset: 0; stroke-dasharray: var(--path-length); }
      }
      .draw-path { animation: drawPath 2s ease-out forwards; }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate-fade-in { animation: fadeIn 1s ease-out forwards; }
      .custom-scrollbar::-webkit-scrollbar { width: 6px; }
      .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
      .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div className="min-h-screen flex flex-col text-white font-inter selection:bg-white/20 relative overflow-x-hidden">
      <ShaderBackground />
      <div className="fixed inset-0 bg-grid-pattern z-[-10] opacity-30 pointer-events-none" />
      <div className="fixed inset-0 bg-gradient-to-b from-transparent via-[#050508]/40 to-[#020203] z-[-10] pointer-events-none" />
      <Analytics />

      {/* Brand Header */}
      <header className="w-full max-w-[1400px] mx-auto px-6 pt-8 lg:px-12 flex items-center justify-between relative z-20 animate-fade-in">
        <div className="flex items-center gap-3 select-none">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-700 flex items-center justify-center shadow-[0_0_15px_rgba(52,211,153,0.3)]">
            <span className="text-black font-outfit font-bold text-lg leading-none mt-0.5">a</span>
          </div>
          <span className="text-2xl font-outfit tracking-wider">
            <span className="font-semibold text-emerald-400">arth</span>
            <span className="font-light text-white/80">matix.</span>
          </span>
        </div>
      </header>

      <main className="flex-grow w-full max-w-[1400px] mx-auto px-6 py-8 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 relative z-10">

        {/* Left Column: Controls */}
        <section className="lg:col-span-5 flex flex-col gap-8 animate-fade-in">
          <div>
            <h1 className="text-4xl md:text-5xl font-outfit font-light tracking-tight mb-2">
              Wealth <span className="text-white/40">Projection</span>
            </h1>
            <p className="text-white/50 font-inter text-sm tracking-wide leading-relaxed">
              Model your financial future across different market scenarios.
              Adjust the parameters to see real-time compounding mechanics.
            </p>
          </div>

          <div className="bg-white/[0.02] backdrop-blur-2xl border border-white/5 p-8 md:p-10 rounded-[3rem] rounded-bl-xl shadow-2xl relative overflow-hidden flex flex-col gap-8">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/[0.03] blur-3xl rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />

            {/* Global Timeline */}
            <div>
              <h3 className="text-white/40 text-xs tracking-widest uppercase mb-6 border-b border-white/10 pb-2">Global Timeline</h3>
              <ElegantSlider label="Current Age" value={age} min={18} max={70} step={1} onChange={setAge} formatVal={(v) => `${v} yrs`} />
              <ElegantSlider label="Retirement Age" value={retireAge} min={age + 1} max={80} step={1} onChange={setRetireAge} formatVal={(v) => `${v} yrs`} />
            </div>

            {/* Portfolio */}
            <div>
              <div className="flex justify-between items-end border-b border-white/10 pb-2 mb-6">
                <h3 className="text-white/40 text-xs tracking-widest uppercase">Your Portfolio</h3>
                <button
                  onClick={addAsset}
                  disabled={assetsLoading}
                  className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors uppercase tracking-wider font-medium flex items-center gap-1 disabled:opacity-40"
                >
                  <span>+</span> Add Asset
                </button>
              </div>

              {assetsError && (
                <p className="text-red-400 text-xs mb-4 font-inter">
                  Failed to load asset classes: {assetsError}
                </p>
              )}

              <div className="flex flex-col gap-6 max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar relative z-10">
                {assets.map((asset) => (
                  <div key={asset.id} className="relative p-6 bg-black/20 rounded-3xl border border-white/5 group transition-colors hover:bg-white/[0.04]">
                    <div className="flex justify-between items-center mb-6">
                      <input
                        type="text"
                        value={asset.name}
                        onChange={(e) => updateAsset(asset.id, 'name', e.target.value)}
                        className="bg-transparent text-lg font-outfit text-white placeholder-white/30 outline-none w-2/3 border-b border-transparent focus:border-white/20 transition-colors"
                      />
                      {assets.length > 1 && (
                        <button
                          onClick={() => removeAsset(asset.id)}
                          className="text-white/20 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 p-1"
                          title="Remove Asset"
                        >✕</button>
                      )}
                    </div>

                    <ElegantSlider label="Monthly SIP" value={asset.sip} min={0} max={500000} step={1000}
                      onChange={(v) => updateAsset(asset.id, 'sip', v)} formatVal={formatINR} />
                    <ElegantSlider label="Lumpsum Base" value={asset.lumpsum} min={0} max={10000000} step={10000}
                      onChange={(v) => updateAsset(asset.id, 'lumpsum', v)} formatVal={formatINR} />

                    <div className="mt-6">
                      <label className="text-sm font-medium text-white/60 tracking-wide uppercase mb-3 block">Asset Class</label>
                      <div className="relative">
                        {assetsLoading ? (
                          <div className="w-full h-12 bg-white/5 rounded-xl animate-pulse" />
                        ) : (
                          <select
                            value={asset.typeId}
                            onChange={(e) => updateAsset(asset.id, 'typeId', e.target.value)}
                            className="w-full appearance-none bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white text-sm font-medium focus:outline-none focus:border-white/30 transition-colors cursor-pointer"
                          >
                            {assetClasses.map((ac) => (
                              <option key={ac.id} value={ac.id} className="bg-[#0a0a0f] text-white">
                                {ac.label} ({((ac.expected_return ?? 0) * 100).toFixed(1)}% Expected)
                              </option>
                            ))}
                          </select>
                        )}
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/40">▼</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Right Column: Projections */}
        <section className="lg:col-span-7 flex flex-col justify-center">
          {projError && (
            <div className="mb-6 px-4 py-3 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-inter">
              {projError}
            </div>
          )}

          {projLoading && !projection && <LoadingPulse />}

          {projection && (
            <div className={`space-y-10 animate-fade-in transition-opacity duration-300 ${projLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
              style={{ animationDelay: '0.2s' }}>

              {/* Primary Output */}
              <div className="relative">
                <div className="hidden lg:block absolute top-1/2 -left-24 w-16 h-[1px] bg-gradient-to-r from-transparent to-white/20" />
                <h2 className="text-sm uppercase tracking-[0.3em] text-white/40 mb-3 ml-2 font-inter">Estimated Net Corpus</h2>
                <div
                  className="text-6xl md:text-8xl font-outfit font-light tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-white to-white/30"
                  style={{ textShadow: `0 0 40px ${accentColor}40` }}
                >
                  {formatINR(projection.total_net_corpus, true)}
                </div>
                {projection.portfolio_cagr != null && (
                  <p className="text-white/30 text-sm font-inter mt-2 ml-2">
                    Portfolio CAGR: <span className="text-emerald-400">{(projection.portfolio_cagr * 100).toFixed(2)}%</span>
                  </p>
                )}
              </div>

              {/* Sparkline from backend yearly_snapshots */}
              {sparklineData && (
                <div className="p-1 -ml-2">
                  <Sparkline data={sparklineData} color={accentColor} />
                </div>
              )}

              {/* Secondary Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <StatCard
                  label="Total Invested"
                  value={formatINR(projection.total_invested, true)}
                  highlightColor="#ffffff"
                />
                <StatCard
                  label="Tax (LTCG Est.)"
                  value={formatINR(projection.total_tax_liability, true)}
                  highlightColor="#ef4444"
                />
                <StatCard
                  label="Purchasing Power"
                  value={formatINR(projection.total_real_net_corpus, true)}
                  subValue={`${retireAge - age}y Inflation-adj.`}
                  highlightColor="#10b981"
                />
                <StatCard
                  label="Gross Returns"
                  value={formatINR(projection.total_gross_corpus - projection.total_invested, true)}
                  highlightColor={accentColor}
                />
              </div>

              {/* Per-asset breakdown */}
              {projection.assets.length > 1 && (
                <div className="space-y-3">
                  <h3 className="text-xs uppercase tracking-widest text-white/30 font-inter">Per Asset Breakdown</h3>
                  {projection.assets.map((a, i) => {
                    const color = assetClasses.find(ac => ac.id === a.asset_id)?.color || ASSET_COLORS[i % ASSET_COLORS.length];
                    return (
                      <div key={a.asset_id + i} className="flex items-center justify-between px-4 py-3 rounded-2xl bg-white/[0.02] border border-white/5">
                        <div className="flex items-center gap-3">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                          <span className="text-sm text-white/70 font-inter">{a.label}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-outfit text-white">{formatINR(a.net_corpus, true)}</span>
                          <span className="text-xs text-white/30 ml-2 font-inter">
                            {a.cagr != null ? `${(a.cagr * 100).toFixed(1)}% CAGR` : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Disclaimer */}
              <div className="mt-12 pt-6 border-t border-white/5 flex items-start gap-4 text-xs leading-relaxed text-white/30 font-inter max-w-2xl">
                <div className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-white/20" />
                <p>
                  <strong className="text-white/50 font-medium">Model Assumptions:</strong> Projections computed by the arthmatix engine using asset-class expected returns, per-instrument tax rules (STCG/LTCG), and a 6% inflation deflator. Past performance is not indicative of future results.
                </p>
              </div>
            </div>
          )}

          {!projection && !projLoading && (
            <div className="h-full flex items-center justify-center text-white/30 font-outfit text-2xl font-light">
              {retireAge <= age ? 'Retirement age must be greater than current age' : 'Configure your portfolio to begin'}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 bg-[#020203]/80 backdrop-blur-xl mt-auto py-12 px-6 lg:px-12 text-sm font-inter animate-fade-in">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-10 text-white/40">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 select-none mb-4">
              <span className="text-xl font-outfit tracking-wider">
                <span className="font-semibold text-emerald-400">arth</span>
                <span className="font-light text-white/80">matix.</span>
              </span>
            </div>
            <p className="leading-relaxed max-w-sm">
              Empowering financial foresight through advanced algorithmic modeling and seamless projections. Master your wealth trajectory with precision.
            </p>
          </div>
          <div>
            <h4 className="text-white/70 font-medium mb-4 uppercase tracking-widest text-xs">Contact Us</h4>
            <p className="hover:text-emerald-400 transition-colors cursor-pointer">hello@arthmatix.com</p>
          </div>
          <div>
            <h4 className="text-white/70 font-medium mb-4 uppercase tracking-widest text-xs">Company Address</h4>
            <p>Arthmatix Technologies Pvt. Ltd.</p>
            <p>Ghaziabad, UP 201102, India</p>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto mt-12 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between items-center text-xs text-white/30">
          <p>&copy; {new Date().getFullYear()} arthmatix. All rights reserved.</p>
          <div className="flex gap-6 mt-4 md:mt-0">
            <a href="#" className="hover:text-white/70 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white/70 transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-white/70 transition-colors">Twitter / X</a>
          </div>
        </div>
      </footer>
    </div>
  );
}